import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, Download, Sparkles, Check, Info } from "lucide-react";
import api from "@/lib/api";
import { useSettings, formatPrice } from "@/lib/settings";
import { useQuoteCalculation } from "@/lib/useQuoteCalculation";
import { submitQuote, downloadOnlyQuote } from "@/lib/quoteSubmit";

const HANDLE_ONLY_TWO = ["Kulplu", "Gizli Kulp"];

export default function PriceCalculatorPage() {
  const [params] = useSearchParams();
  const initialTab = params.get("mode") === "pro" ? "professional" : "standard";
  const [tab, setTab] = useState(initialTab);
  const initialSlug = params.get("tur");

  const [types, setTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [covers, setCovers] = useState([]);
  const [legs, setLegs] = useState([]);
  const [handles, setHandles] = useState([]);
  const [pricing, setPricing] = useState({ unit_price_standard: 3500, unit_price_professional: 5500, currency: "TL" });

  const { settings } = useSettings();

  const [furnitureId, setFurnitureId] = useState("");
  const [variation, setVariation] = useState("");
  const [width, setWidth] = useState(200);
  const [height, setHeight] = useState(220);
  const [depth, setDepth] = useState(60);
  const [colorName, setColorName] = useState("");
  const [coverName, setCoverName] = useState("");
  const [legName, setLegName] = useState("");
  const [handleName, setHandleName] = useState("Kulplu");
  const [extras, setExtras] = useState({});

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [t, c, cv, l, h, p] = await Promise.all([
        api.get("/furniture-types", { params: { active_only: true } }).then((r) => r.data),
        api.get("/colors", { params: { active_only: true } }).then((r) => r.data),
        api.get("/cover-types", { params: { active_only: true } }).then((r) => r.data),
        api.get("/leg-types", { params: { active_only: true } }).then((r) => r.data),
        api.get("/handle-types", { params: { active_only: true } }).then((r) => r.data),
        api.get("/pricing").then((r) => r.data),
      ]);
      if (cancelled) return;
      setTypes(t); setColors(c); setCovers(cv); setLegs(l); setHandles(h); setPricing(p);
      const pre = t.find((x) => x.slug === initialSlug) || t[0];
      if (pre) setFurnitureId(pre.id);
      if (c[0]) setColorName(c[0].name);
      if (l[0]) setLegName(l[0].name);
      setCoverName(initialTab === "standard" ? "MDFlam" : (cv[0]?.name || ""));
    })();
    return () => { cancelled = true; };
  }, [initialSlug, initialTab]);

  const furniture = useMemo(() => types.find((t) => t.id === furnitureId), [types, furnitureId]);

  // Filter cover types based on tier
  const filteredCovers = useMemo(() => {
    if (tab === "standard") return covers.filter((c) => c.tier === "standard" || c.tier === "both");
    return covers.filter((c) => c.tier === "professional" || c.tier === "both");
  }, [covers, tab]);

  const filteredLegs = useMemo(() => {
    if (!furniture?.allowed_leg_ids?.length) return legs;
    return legs.filter((l) => furniture.allowed_leg_ids.includes(l.id));
  }, [legs, furniture]);

  // Reset cover selection when tab changes
  useEffect(() => {
    if (tab === "standard") { setCoverName("MDFlam"); return; }
    if (filteredCovers[0] && !filteredCovers.find((c) => c.name === coverName)) {
      setCoverName(filteredCovers[0].name);
    }
  }, [tab, filteredCovers, coverName]);

  // In Standard mode use the first active leg/handle as the default (admin-controlled via order)
  const defaultLegName = legs[0]?.name || "";
  const defaultHandleName = handles[0]?.name || "";
  const effLegName = tab === "standard" ? defaultLegName : legName;
  const effHandleName = tab === "standard" ? defaultHandleName : handleName;

  const calc = useQuoteCalculation({ tab, width, height, coverName, legName: effLegName, handleName: effHandleName, extras, types, furnitureId, variation, covers, legs, handles, pricing });
  const selectedColor = useMemo(() => colors.find((c) => c.name === colorName), [colors, colorName]);

  const buildPayload = () => {
    if (!furniture) { toast.error("Lütfen mobilya türünü seçin"); return null; }
    const effColor = tab === "standard" ? (colorName || "Beyaz") : colorName;
    if (tab === "professional" && (!colorName || !legName || !handleName)) {
      toast.error("Tüm seçimleri yapın");
      return null;
    }
    const payload = {
      tier: tab,
      furniture_type_id: furniture.id,
      furniture_type_name: furniture.name,
      variation: tab === "professional" ? variation : undefined,
      width_cm: Number(width) || 0,
      height_cm: Number(height) || 0,
      depth_cm: Number(depth) || 0,
      color_name: effColor,
      color_hex: selectedColor?.hex || "",
      cover_type_name: coverName || "MDFlam",
      leg_type_name: effLegName,
      handle_type_name: effHandleName,
      extras,
    };
    const ctx = { tab, furniture, variation, width, height, depth, colorName: effColor, coverName: coverName || "MDFlam", legName: effLegName, handleName: effHandleName, currency: pricing.currency };
    return { payload, ctx };
  };

  const submit = async () => {
    const built = buildPayload();
    if (!built) return;
    setSubmitting(true);
    try {
      await submitQuote({ payload: built.payload, ctx: built.ctx, whatsappNumber: settings.whatsapp_number });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  const download = async () => {
    const built = buildPayload();
    if (!built) return;
    setSubmitting(true);
    try {
      await downloadOnlyQuote({ payload: built.payload });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="price-calculator-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">Fiyat Hesaplama</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink-900">
          Ölçünüzü Girin, <span className="gradient-text">Anlık Teklifiniz Hazır</span>
        </h1>
        <p className="mt-3 text-ink-500 max-w-2xl">Basit hızlı hesap için <b>Standart</b>, detaylı proje teklifi için <b>Profesyonel</b> sekmesini kullanın.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-ink-100 rounded-full p-1 h-12 gap-1" data-testid="calculator-tabs">
          <TabsTrigger value="standard" data-testid="tab-standard" className="rounded-full text-ink-700 data-[state=active]:!bg-gradient-to-r data-[state=active]:!from-brand-500 data-[state=active]:!to-brand-700 data-[state=active]:!text-white px-6 h-10 text-sm font-semibold">
            Standart
          </TabsTrigger>
          <TabsTrigger value="professional" data-testid="tab-professional" className="rounded-full text-ink-700 data-[state=active]:!bg-gradient-to-r data-[state=active]:!from-brand-500 data-[state=active]:!to-brand-700 data-[state=active]:!text-white px-6 h-10 text-sm font-semibold">
            Profesyonel
          </TabsTrigger>
        </TabsList>


        <div className="mt-8 space-y-6 max-w-4xl">
          <TabsContent value="standard" className="mt-0 space-y-6">
            <StandardWizard
              types={types}
              furnitureId={furnitureId}
              setFurnitureId={setFurnitureId}
              width={width} setWidth={setWidth}
              height={height} setHeight={setHeight}
              depth={depth} setDepth={setDepth}
            />
            <PriceOrderSection
              stepNumber={3}
              calc={calc}
              pricing={pricing}
              furniture={furniture}
              selectedColor={selectedColor}
              tab={tab}
              submit={submit}
              download={download}
              submitting={submitting}
              coverName={coverName}
              colorName={colorName}
            />
          </TabsContent>
          <TabsContent value="professional" className="mt-0 space-y-6">
            <ProfessionalConfigurator
              types={types}
              colors={colors}
              covers={filteredCovers}
              legs={filteredLegs}
              furnitureId={furnitureId} setFurnitureId={setFurnitureId}
              variation={variation} setVariation={setVariation}
              width={width} setWidth={setWidth}
              height={height} setHeight={setHeight}
              depth={depth} setDepth={setDepth}
              colorName={colorName} setColorName={setColorName}
              coverName={coverName} setCoverName={setCoverName}
              legName={legName} setLegName={setLegName}
              handleName={handleName} setHandleName={setHandleName}
              extras={extras} setExtras={setExtras}
            />
            <PriceOrderSection
              stepNumber={7}
              calc={calc}
              pricing={pricing}
              furniture={furniture}
              selectedColor={selectedColor}
              tab={tab}
              submit={submit}
              download={download}
              submitting={submitting}
              coverName={coverName}
              colorName={colorName}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function PriceOrderSection({ stepNumber, calc, pricing, furniture, selectedColor, tab, submit, download, submitting, coverName, colorName }) {
  return (
    <Card className="border-2 border-brand-300 rounded-3xl overflow-hidden brand-shadow" data-testid="price-order-section">
      <CardContent className="p-7">
        <div className="flex items-center gap-3 mb-5">
          <span className="grid h-8 w-8 place-items-center rounded-full gradient-primary text-white text-sm font-bold">{stepNumber}</span>
          <h3 className="font-display font-bold text-xl text-ink-900">Tahmini Fiyat ve Sipariş</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {furniture?.preview_url && (
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-brand-50 border border-brand-100">
              <img key={furniture.id + (coverName || "") + (colorName || "")} src={furniture.preview_url} alt={furniture.name} className="w-full h-full object-cover animate-fade-up" />
              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-bold text-brand-700">
                  {tab === "professional" ? "PROFESYONEL" : "STANDART"}
                </span>
                {selectedColor && (
                  <span className="rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-semibold text-ink-700 flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full border border-ink-200" style={{ background: selectedColor.hex }} />
                    {selectedColor.name}
                  </span>
                )}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-ink-500">Tahmini Fiyat</div>
            <div className="mt-1 font-display font-black text-4xl md:text-5xl gradient-text" data-testid="live-price">
              {formatPrice(calc.total, pricing.currency)}
            </div>
            <div className="mt-1 text-xs text-ink-500">
              {calc.area.toFixed(2)} m² · {formatPrice(calc.unit, pricing.currency)}/m²
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-ink-500">
              {calc.coverMult !== 1 && <div className="flex justify-between"><span>Kapak çarpanı</span><span>x {calc.coverMult}</span></div>}
              {calc.legTotal > 0 && <div className="flex justify-between"><span>Ayak</span><span>+{formatPrice(calc.legTotal, pricing.currency)}</span></div>}
              {calc.handleTotal > 0 && <div className="flex justify-between"><span>Kulp</span><span>+{formatPrice(calc.handleTotal, pricing.currency)}</span></div>}
              {calc.extrasTotal > 0 && <div className="flex justify-between"><span>Ekstra modüller</span><span>+{formatPrice(calc.extrasTotal, pricing.currency)}</span></div>}
            </div>
            <Button
              data-testid="submit-quote-btn"
              onClick={submit}
              disabled={submitting || !furniture}
              className="mt-6 w-full gradient-primary text-white rounded-full h-12 font-bold uppercase text-sm tracking-wide hover:brightness-110 hover:-translate-y-0.5 transition-transform brand-shadow"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {submitting ? "Hazırlanıyor..." : "Sipariş Vermek İstiyorum"}
            </Button>
            <Button
              data-testid="download-quote-btn"
              onClick={download}
              disabled={submitting || !furniture}
              variant="outline"
              className="mt-2 w-full rounded-full h-11 font-bold uppercase text-sm tracking-wide border-2 border-brand-300 text-brand-700 hover:bg-brand-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Teklifi İndir (PDF)
            </Button>
            <div className="mt-3 flex items-center justify-center gap-1 text-xs text-ink-500">
              <Download className="h-3.5 w-3.5" /> PDF teklif otomatik oluşturulur; siparişte WhatsApp&apos;a yönlendirilirsiniz
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/* ---------- Sub components ---------- */

function SectionCard({ number, title, subtitle, children, testid }) {
  return (
    <Card className="border-ink-100 rounded-3xl overflow-hidden" data-testid={testid}>
      <CardContent className="p-7">
        <div className="flex items-center gap-3 mb-5">
          <span className="grid h-8 w-8 place-items-center rounded-full gradient-primary text-white text-sm font-bold">{number}</span>
          <div>
            <h3 className="font-display font-bold text-xl text-ink-900">{title}</h3>
            {subtitle && <p className="text-xs text-ink-500">{subtitle}</p>}
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function TypeGrid({ types, furnitureId, setFurnitureId, onPick }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {types.map((t) => {
        const active = t.id === furnitureId;
        return (
          <button
            key={t.id}
            data-testid={`type-option-${t.slug}`}
            onClick={() => { setFurnitureId(t.id); onPick?.(t); }}
            className={`relative overflow-hidden rounded-2xl border-2 text-left transition-all ${active ? "border-brand-500 brand-shadow" : "border-ink-100 hover:border-brand-300"}`}
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={t.image_url} alt={t.name} className="h-full w-full object-cover" />
            </div>
            <div className="p-3">
              <div className="text-sm font-semibold text-ink-900">{t.name}</div>
            </div>
            {active && <span className="absolute top-2 right-2 grid h-6 w-6 place-items-center rounded-full bg-brand-500 text-white"><Check className="h-3.5 w-3.5" /></span>}
          </button>
        );
      })}
    </div>
  );
}

function InfoBanner({ text }) {
  return (
    <div
      className="mt-5 flex items-start gap-3 rounded-2xl border border-brand-100 bg-brand-50/70 px-4 py-3 text-sm text-brand-800"
      data-testid="type-info-banner"
    >
      <Info className="h-4 w-4 mt-0.5 shrink-0 text-brand-600" />
      <div className="leading-relaxed">{text}</div>
    </div>
  );
}


function DimensionsBlock({ width, setWidth, height, setHeight, depth, setDepth, mode = "wh" }) {
  const m = mode || "wh";
  const showW = m.includes("w");
  const showH = m.includes("h");
  const showD = m === "whd";  const dims = [];
  if (showW) dims.push(["Genişlik", width, setWidth, "width"]);
  if (showH) dims.push(["Yükseklik", height, setHeight, "height"]);
  if (showD) dims.push(["Derinlik", depth, setDepth, "depth"]);
  if (dims.length === 0) dims.push(["Genişlik", width, setWidth, "width"]);
  const cols = dims.length === 1 ? "grid-cols-1" : dims.length === 2 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className={`grid ${cols} gap-4`} data-testid="dim-block">
      {dims.map(([label, val, setter, key]) => (
        <div key={key}>
          <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">{label} (cm)</Label>
          <Input
            data-testid={`dim-${key}`}
            type="number"
            min={20}
            max={800}
            value={val === 0 || val === "" ? "" : val}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const raw = e.target.value;
              setter(raw === "" ? 0 : Number(raw));
            }}
            className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500"
            placeholder={label === "Genişlik" ? "örn. 300" : label === "Yükseklik" ? "örn. 220" : "örn. 60"}
          />
        </div>
      ))}
    </div>
  );
}

function ColorSwatches({ colors, colorName, setColorName }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
      {colors.map((c) => {
        const active = c.name === colorName;
        return (
          <button
            key={c.id}
            data-testid={`color-${c.name}`}
            onClick={() => setColorName(c.name)}
            className={`group text-left rounded-2xl border-2 p-2 transition-all ${active ? "border-brand-500" : "border-ink-100 hover:border-brand-300"}`}
          >
            <div className="aspect-square rounded-xl border border-ink-100" style={{ background: c.hex }} />
            <div className="mt-2 text-[11px] font-semibold text-ink-700 leading-tight">{c.name}</div>
          </button>
        );
      })}
    </div>
  );
}

function OptionButtons({ items, value, setValue, testidPrefix, getExtra }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = it.name === value;
        return (
          <button
            key={it.id || it.name}
            data-testid={`${testidPrefix}-${it.name}`}
            onClick={() => setValue(it.name)}
            className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-semibold transition-all ${active ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700 hover:border-brand-300"}`}
          >
            {it.name}
            {getExtra ? getExtra(it) : null}
          </button>
        );
      })}
    </div>
  );
}

function StandardWizard({ types, furnitureId, setFurnitureId, width, setWidth, height, setHeight, depth, setDepth }) {
  const currentFt = types.find((t) => t.id === furnitureId);
  const mode = currentFt?.dimension_mode || "wh";
  return (
    <>
      <SectionCard number={1} title="Mobilya Türünü Seç" testid="standard-step-type">
        <TypeGrid types={types} furnitureId={furnitureId} setFurnitureId={setFurnitureId} />
        {currentFt?.info_text && <InfoBanner text={currentFt.info_text} />}
      </SectionCard>
      <SectionCard number={2} title="Ölçüler" testid="standard-step-dims">
        <DimensionsBlock width={width} setWidth={setWidth} height={height} setHeight={setHeight} depth={depth} setDepth={setDepth} mode={mode} />
      </SectionCard>
      <div className="rounded-2xl border border-brand-100 bg-brand-50/60 px-5 py-4 text-sm text-brand-800 flex items-start gap-3">
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <b>Standart paket</b>: MDFlam malzeme, standart <b>Beyaz</b> renk, <b>Baza</b> ayak ve <b>Kulplu</b> tercihi ile hızlı fiyatlandırma. Renk, kapak veya ayak seçenekleri için <b>Profesyonel</b> sekmesine geçin.
        </div>
      </div>
    </>
  );
}

function ProfessionalConfigurator({ types, colors, covers, legs, furnitureId, setFurnitureId, variation, setVariation, width, setWidth, height, setHeight, depth, setDepth, colorName, setColorName, coverName, setCoverName, legName, setLegName, handleName, setHandleName, extras, setExtras }) {
  const furniture = types.find((t) => t.id === furnitureId);
  const mode = furniture?.dimension_mode || "wh";
  const setExtra = (key, value) => setExtras((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <SectionCard number={1} title="Mobilya Türü & Alt Model" testid="pro-step-type">
        <TypeGrid types={types} furnitureId={furnitureId} setFurnitureId={setFurnitureId} onPick={() => { setVariation(""); setExtras({}); }} />
        {furniture?.info_text && <InfoBanner text={furniture.info_text} />}
        {furniture?.variations?.length > 0 && (
          <div className="mt-5">
            <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Alt Model</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {furniture.variations.map((v) => {
                const name = typeof v === "string" ? v : v?.name;
                const extra = typeof v === "object" ? Number(v?.extra_price || 0) : 0;
                const mode = typeof v === "object" ? v?.price_mode : "flat";
                if (!name) return null;
                return (
                  <button
                    key={name}
                    data-testid={`variation-${name}`}
                    onClick={() => setVariation(name)}
                    className={`inline-flex items-center gap-1.5 rounded-full border-2 px-4 py-1.5 text-xs font-semibold transition-all ${variation === name ? "border-brand-500 bg-brand-50 text-brand-700" : "border-ink-100 text-ink-700 hover:border-brand-300"}`}
                  >
                    {name}
                    {extra > 0 && <span className="text-[10px] font-bold text-brand-600">+{extra.toLocaleString("tr-TR")} TL{mode === "per_m2" ? "/m²" : ""}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard number={2} title="Ölçüler" testid="pro-step-dims">
        <DimensionsBlock width={width} setWidth={setWidth} height={height} setHeight={setHeight} depth={depth} setDepth={setDepth} mode={mode} />
      </SectionCard>

      <SectionCard number={3} title="Kapak Türü" subtitle="Malzemeye göre fiyat çarpanı uygulanır" testid="pro-step-cover">
        <OptionButtons
          items={covers}
          value={coverName}
          setValue={setCoverName}
          testidPrefix="cover"
          getExtra={(c) => c.multiplier && c.multiplier !== 1 ? <span className="text-[10px] font-bold text-brand-600">x{c.multiplier}</span> : null}
        />
      </SectionCard>

      <SectionCard number={4} title="Renk (20 seçenek)" testid="pro-step-color">
        <ColorSwatches colors={colors} colorName={colorName} setColorName={setColorName} />
      </SectionCard>

      <SectionCard number={5} title="Ayak & Kulp" testid="pro-step-leg-handle">
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Ayak Türü</Label>
            <div className="mt-2"><OptionButtons items={legs} value={legName} setValue={setLegName} testidPrefix="leg" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Kulp Türü</Label>
            <div className="mt-2"><OptionButtons items={HANDLE_ONLY_TWO.map((n) => ({ name: n }))} value={handleName} setValue={setHandleName} testidPrefix="handle" /></div>
          </div>
        </div>
      </SectionCard>

      {furniture?.extra_fields?.length > 0 && (
        <SectionCard number={6} title="Özel Ekstralar" subtitle={`${furniture.name} için opsiyonel seçenekler · varsayılan "Yok"`} testid="pro-step-extras">
          <div className="grid sm:grid-cols-2 gap-4">
            {furniture.extra_fields.map((f) => {
              const normOpts = (f.options || []).map((o) =>
                typeof o === "string"
                  ? { name: o, extra_price: 0, price_mode: "flat" }
                  : { name: o.name, extra_price: Number(o.extra_price || 0), price_mode: o.price_mode || "flat" }
              );
              const current = extras[f.key]?.name || (normOpts.find((o) => o.name === "Yok")?.name || normOpts[0]?.name || "");
              return (
                <div key={f.key}>
                  <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">{f.label}</Label>
                  <Select
                    value={current}
                    onValueChange={(v) => {
                      const opt = normOpts.find((o) => o.name === v);
                      setExtra(f.key, { name: v, price: opt?.extra_price || 0, mode: opt?.price_mode || "flat" });
                    }}
                  >
                    <SelectTrigger data-testid={`extra-${f.key}`} className="mt-1.5 rounded-xl border-ink-100">
                      <SelectValue placeholder="Seçim yapın" />
                    </SelectTrigger>
                    <SelectContent>
                      {normOpts.map((o) => (
                        <SelectItem key={o.name} value={o.name}>
                          {o.name}
                          {o.extra_price > 0
                            ? ` (+${o.extra_price.toLocaleString("tr-TR")} TL${o.price_mode === "per_m2" ? "/m²" : ""})`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </>
  );
}



